import React from "react";
import { Compass, ArrowRight, ShieldCheck, MessageSquare } from "lucide-react";

export default function GenericPage({ tab, onTabChange }) {
  // Map page titles and content dynamically
  const pageDetails = {
    solution_b2b_enterprises: {
      category: "Enterprise Supply Chain",
      title: "B2B Enterprises Supply Chain",
      subtitle: "Bulk Industrial Movements",
      desc: "Seamless, technology-backed corporate Supply Chain. Integrated supply chains, automatic inventory distribution algorithms, and heavy contract fleet coordination.",
      statsTitle: "Corporate Scope",
      stats: {
        "Active Clients": "12,000+ Enterprises",
        "Consolidated SLA": "99.9% Compliance",
        "Invoice Remittance": "Automated Weekly",
        "API SLA": "99.99% Uptime",
      },
    },
    solution_rto_predictor: {
      category: "Risk Analytics",
      title: "RTO Predictor Engine",
      subtitle: "Return to Origin Risk Modeling",
      desc: "Leverage spatial and buyer behavioral machine learning models to identify and flag high-risk cod transactions, saving thousands in Supply Chain fees.",
      statsTitle: "Engine Accuracy",
      stats: {
        "RTO Reduction": "Up to 24%",
        "Query Latency": "&lt; 40ms",
        "Address Match": "99% Cleansed",
        "Profile Models": "50M+ buyer histories",
      },
    },
    partner_delivery_partner: {
      category: "Join Us",
      title: "Delivery Partner Program",
      subtitle: "Become a Last-Mile Hub Operator",
      desc: "Run last-mile operations in your commercial neighborhood. JPSY provides automated routing software, sorting support, and predictable monthly cargo dispatches.",
      statsTitle: "Partner Benefits",
      stats: {
        "Average Commissions": "₹20 - ₹45 per delivery",
        "Payout Frequency": "Every Thursday",
        "Setup Assistance": "Complete Local Store Signage",
        "Software Licensing": "Complementary JPSY App",
      },
    },
    partner_fleet_owners: {
      category: "Haulage Attachment",
      title: "Fleet Owners Program",
      subtitle: "Attach your Truck or Container Fleet",
      desc: "Connect your trucks, chassis, and container vehicles directly to our national linehaul algorithms. Earn continuous guaranteed mileage fees with zero empty-backhaul runs.",
      statsTitle: "Fleet Integration",
      stats: {
        "Minimum Vehicles": "1 container truck",
        "Standard Rates": "Predictive price per km",
        "Fuel Allowances": "Up to 35% subsidies",
        "GPS Setup": "Complementary Smart Lock",
      },
    },
    company_governance: {
      category: "Corporate Desk",
      title: "Governance & Leadership",
      subtitle: "Ethical, Board-Led Administration",
      desc: "Committed to transparent and sustainable operations. Built on robust regulatory filings, continuous environmental auditing, and multi-layered board supervision.",
      statsTitle: "Board Structure",
      stats: {
        "Independent Directors": "60% of Board",
        "Audit Frequency": "Quarterly external oversight",
        "Sustainability Score": "Silver Rated ESG",
        "Compliance Ratio": "100% Tax & E-way filings",
      },
    },
    company_press_release: {
      category: "Media Room",
      title: "Press Releases & News",
      subtitle: "Corporate updates & Industry announcements",
      desc: "Read official publications, corporate partnership announcements, Supply Chain technology insights, and regional launch bulletins from the JPSY Desk.",
      statsTitle: "Recent News",
      stats: {
        "Mega Sorter Hub": "Launched in Hyderabad NCR",
        "EV last-mile fleet": "Expanded by 5,000 units",
        "Air Charter Network": "Dubai route frequency doubled",
        "Series-B Expansion": "Completed successfully",
      },
    },
    company_careers: {
      category: "Build the Future",
      title: "Careers at JPSY",
      subtitle: "Join India's Most Advanced Supply Chain Team",
      desc: "We are continuously seeking skilled software developers, geospatial analysts, industrial operations experts, and regional hub leads to power the future of Supply Chain.",
      statsTitle: "Work Culture",
      stats: {
        "Active Roles Open": "45+ Engineering & Ops",
        "Work Location": "Bengaluru, Mumbai, Delhi NCR",
        "Annual Benefits": "Full Family Medical + Stock options",
        "Growth Corridor": "Mentorship-driven engineering",
      },
    },
    investors: {
      category: "Investor Relations",
      title: "Investor Relations desk",
      subtitle: "Public Filings & Financial Reports",
      desc: "Comprehensive financial updates, quarterly earnings reports, shareholder presentation decks, annual regulatory filings, and corporate growth trajectories.",
      statsTitle: "Performance Snapshot",
      stats: {
        "EBITDA Growth": "+34% Year-over-Year",
        "Annual Shipments": "1.2 Billion Packages",
        "Global Corridors": "Expanded to 5 major countries",
        "Pincode Footprint": "19,500 active grids",
      },
    },
    support: {
      category: "Customer Support",
      title: "Support & Help Desk",
      subtitle: "Raise shipment queries and resolve disputes",
      desc: "Our customer support center is available 24/7 to resolve tracking delays, address updates, wallet top-ups, and COD claims immediately.",
      statsTitle: "Response Times",
      stats: {
        "Chat Support": "Immediate response (24/7)",
        "Merchant Ticket SLA": "&lt; 4 hours",
        "Call Center": "1800-JPSY-SUPPORT",
        "Self-Serve Portal": "Automatic address correction online",
      },
    },
  };

  const activePage = pageDetails[tab] || {
    category: "Information Page",
    title: "Custom JPSY Services",
    subtitle: "Advanced Transport networks",
    desc: "India's fastest automated commercial Supply Chain and routing suite.",
    statsTitle: "Standard SLAs",
    stats: {
      "Service Coverage": "Pan-India",
      "Software Integration": "1-Click API",
      "Support SLA": "24/7 Chat Desk",
      "Performance Rating": "99.8% On-Time",
    },
  };

  return (
    <div className="flex-1 bg-white font-sans">
      {/* Delhivery Style Dark Header */}
      <section className="bg-[#111215] text-white py-16 px-6 relative overflow-hidden border-b-4 border-[#263f98]">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#263f98]/10 rounded-full filter blur-3xl" />
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-12 relative z-10">
          <div className="space-y-6 max-w-2xl">
            <span className="bg-[#263f98] text-white text-[10px] uppercase font-bold tracking-widest px-3 py-1 rounded">
              {activePage.category}
            </span>
            <h1 className="text-4xl sm:text-5xl font-sans font-black tracking-tight leading-tight">
              {activePage.title}
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed font-sans font-medium">
              {activePage.subtitle}
            </p>
            <p className="text-slate-400 text-sm leading-relaxed">
              {activePage.desc}
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => onTabChange("dashboard")}
                className="bg-[#263f98] hover:bg-[#1f2f82] active:bg-[#21185f] text-white font-bold px-6 py-3 rounded-xl transition-colors shadow-lg flex items-center gap-2 text-sm cursor-pointer"
              >
                <span>Access Merchant Portal</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="w-full md:w-80 bg-slate-900 border border-slate-800 rounded-2xl p-6 text-slate-300 space-y-4">
            <h3 className="font-bold text-white font-sans text-sm tracking-wider uppercase border-b border-slate-800 pb-2">
              {activePage.statsTitle}
            </h3>
            <div className="space-y-3 text-xs">
              {Object.entries(activePage.stats).map(([label, value]) => (
                <div key={label} className="flex justify-between gap-4">
                  <span className="text-slate-400">{label}</span>
                  <span className="font-bold text-white text-right">
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Grid of details */}
      <section className="py-20 max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="bg-slate-50 border border-slate-200 p-8 rounded-2xl space-y-4">
          <div className="w-12 h-12 rounded-xl bg-[#263f98]/10 text-[#263f98] flex items-center justify-center font-bold">
            <Compass className="w-6 h-6" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 font-sans">
            Tech-First Dispatch
          </h3>
          <p className="text-sm text-slate-500 leading-relaxed">
            Every step is automated. From barcode scans to dynamic route
            assignments, our machine learning Supply Chain algorithms eliminate
            human sorting errors entirely.
          </p>
        </div>

        <div className="bg-slate-50 border border-slate-200 p-8 rounded-2xl space-y-4">
          <div className="w-12 h-12 rounded-xl bg-[#263f98]/10 text-[#263f98] flex items-center justify-center font-bold">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 font-sans">
            Complete Cargo Shield
          </h3>
          <p className="text-sm text-slate-500 leading-relaxed">
            We provide full transit insurance and tamper-proof secure packing
            slips. Ship heavy palettes, delicate electronics, or high-value
            documents with absolute assurance.
          </p>
        </div>

        <div className="bg-slate-50 border border-slate-200 p-8 rounded-2xl space-y-4">
          <div className="w-12 h-12 rounded-xl bg-[#263f98]/10 text-[#263f98] flex items-center justify-center font-bold">
            <MessageSquare className="w-6 h-6" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 font-sans">
            24/7 Dedicated Helpline
          </h3>
          <p className="text-sm text-slate-500 leading-relaxed">
            Never stay in the dark. Receive instant WhatsApp, SMS, and Email
            milestones, backed by dedicated telephone helpline operators to
            resolve any regional exceptions instantly.
          </p>
        </div>
      </section>
    </div>
  );
}
